import { z } from 'zod';
import { mitigationDryingIdSchema, mitigationDryingReasonSchema, } from './mitigation-drying-workspace.contract.js';
import { mitigationFieldEvidenceReferenceSchema } from './mitigation-field-completion.contract.js';
export const mitigationContentsInventoryCapability = 'mitigation.contents-inventory.v1';
export const mitigationContentsCategorySchema = z.enum([
    'furniture',
    'electronics',
    'appliances',
    'clothing',
    'documents',
    'tools',
    'sports',
    'toys',
    'art',
    'jewelry',
    'general',
    'other',
]);
export const mitigationContentsConditionSchema = z.enum([
    'excellent', 'good', 'fair', 'poor',
]);
export const mitigationContentsHandlingStatusSchema = z.enum([
    'documented',
    'packed-out',
    'cleaning',
    'stored',
    'returned',
    'disposed',
]);
export const mitigationContentsCostInputSchema = z.strictObject({
    replacementUnitCostCents: z.number().int().min(0).max(1_000_000_000),
    annualDepreciationBasisPoints: z.number().int().min(0).max(10_000),
    currency: z.string().trim().regex(/^[A-Z]{3}$/),
});
export const mitigationContentsItemInputSchema = z.strictObject({
    roomLabel: z.string().trim().min(1).max(240),
    itemName: z.string().trim().min(1).max(240),
    description: z.string().trim().min(1).max(2_000).nullable().default(null),
    category: mitigationContentsCategorySchema,
    quantity: z.number().int().min(1).max(100_000),
    ageMonths: z.number().int().min(0).max(2_400),
    condition: mitigationContentsConditionSchema,
    handlingStatus: mitigationContentsHandlingStatusSchema.default('documented'),
    locationLabel: z.string().trim().min(1).max(240).nullable().default(null),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema).max(20).default([]),
    notes: z.string().trim().min(1).max(5_000).nullable().default(null),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    cost: mitigationContentsCostInputSchema,
});
const contentsCommandBase = {
    commandId: mitigationDryingIdSchema,
    itemId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
};
export const mitigationContentsItemCommandSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        ...contentsCommandBase,
        kind: z.literal('create'),
        expectedRevision: z.null(),
        data: mitigationContentsItemInputSchema,
    }),
    z.strictObject({
        ...contentsCommandBase,
        kind: z.literal('update'),
        expectedRevision: z.number().int().positive(),
        data: mitigationContentsItemInputSchema,
    }),
    z.strictObject({
        ...contentsCommandBase,
        kind: z.literal('void'),
        expectedRevision: z.number().int().positive(),
        data: z.null(),
    }),
]);
export const mitigationContentsBulkCreateCommandSchema = z.strictObject({
    batchId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
    items: z.array(z.strictObject({
        commandId: mitigationDryingIdSchema,
        itemId: mitigationDryingIdSchema,
        data: mitigationContentsItemInputSchema,
    })).min(1).max(200),
}).superRefine((value, context) => {
    if (new Set(value.items.map(({ commandId }) => commandId)).size !== value.items.length) {
        context.addIssue({ code: 'custom', path: ['items'], message: 'Bulk command IDs must be unique.' });
    }
    if (new Set(value.items.map(({ itemId }) => itemId)).size !== value.items.length) {
        context.addIssue({ code: 'custom', path: ['items'], message: 'Bulk item IDs must be unique.' });
    }
});
export const mitigationContentsCostSchema = mitigationContentsCostInputSchema.extend({
    unitDepreciationCents: z.number().int().min(0),
    unitActualCashValueCents: z.number().int().min(0),
    replacementTotalCents: z.number().int().min(0),
    depreciationTotalCents: z.number().int().min(0),
    actualCashValueTotalCents: z.number().int().min(0),
});
export const mitigationContentsItemSchema = z.strictObject({
    contractVersion: z.literal(mitigationContentsInventoryCapability),
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    revision: z.number().int().positive(),
    item: mitigationContentsItemInputSchema.omit({ cost: true }),
    cost: mitigationContentsCostSchema.nullable(),
    reason: mitigationDryingReasonSchema,
    commandId: mitigationDryingIdSchema,
    recordedByMembershipId: mitigationDryingIdSchema,
    recordedAt: z.string().datetime({ offset: true }),
    createdByMembershipId: mitigationDryingIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    void: z.strictObject({
        commandId: mitigationDryingIdSchema,
        reason: mitigationDryingReasonSchema,
        actorMembershipId: mitigationDryingIdSchema,
        voidedAt: z.string().datetime({ offset: true }),
    }).nullable(),
});
export const mitigationContentsListQuerySchema = z.strictObject({
    query: z.string().trim().max(240).default(''),
    roomLabel: z.string().trim().max(240).default(''),
    category: mitigationContentsCategorySchema.nullable().default(null),
    handlingStatus: mitigationContentsHandlingStatusSchema.nullable().default(null),
    includeVoided: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const pageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const mitigationContentsItemsResponseSchema = z.strictObject({
    data: z.array(mitigationContentsItemSchema),
    page: pageSchema,
    costsVisible: z.boolean(),
});
export const mitigationContentsItemResponseSchema = z.strictObject({
    data: mitigationContentsItemSchema,
    command: z.strictObject({ commandId: mitigationDryingIdSchema, idempotent: z.boolean() }).optional(),
});
export const mitigationContentsBulkResponseSchema = z.strictObject({
    data: z.array(mitigationContentsItemSchema),
    batchId: mitigationDryingIdSchema,
    idempotentCount: z.number().int().nonnegative(),
});
export const mitigationContentsHistoryResponseSchema = z.strictObject({
    data: z.array(mitigationContentsItemSchema),
    costsVisible: z.boolean(),
});
export const mitigationContentsSummaryRowSchema = z.strictObject({
    groupBy: z.enum(['room', 'category', 'handling-status']),
    groupKey: z.string().trim().min(1).max(240),
    itemCount: z.number().int().nonnegative(),
    unitCount: z.number().int().nonnegative(),
    replacementTotalCents: z.number().int().nonnegative().nullable(),
    depreciationTotalCents: z.number().int().nonnegative().nullable(),
    actualCashValueTotalCents: z.number().int().nonnegative().nullable(),
});
export const mitigationContentsSummaryResponseSchema = z.strictObject({
    data: z.array(mitigationContentsSummaryRowSchema),
    costsVisible: z.boolean(),
});
export const mitigationContentsExportResponseSchema = z.strictObject({
    contractVersion: z.literal('mitigation.contents-export.v1'),
    projectId: mitigationDryingIdSchema,
    costsVisible: z.boolean(),
    columns: z.array(z.string().trim().min(1)),
    rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
});
//# sourceMappingURL=mitigation-contents-inventory.contract.js.map